import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Consultation() {
  // SEO Configuration
  useSEO({
    title: 'Expert Consultation - The Villarreal Health Network',
    description: 'Schedule a consultation with our expert care coordinators. Get personalized guidance for home healthcare services, insurance navigation, and care planning in Las Vegas.',
    keywords: 'healthcare consultation, care coordinator, expert guidance, home health consultation, care planning services Las Vegas',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "MedicalBusiness",
          "name": "Expert Healthcare Consultation",
          "description": "Professional consultation services for home healthcare planning",
          "provider": getOrganizationSchema(),
          "availableService": {
            "@type": "MedicalProcedure",
            "name": "Healthcare Consultation",
            "description": "Expert guidance and care coordination consultation"
          }
        },
        getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Expert Consultation', url: '/consultation' }
        ])
      ]
    }
  });

  const [selectedTime, setSelectedTime] = useState('');
  const [consultationType, setConsultationType] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    preferredContact: 'phone',
    urgency: 'routine',
    consultationTopic: '',
    currentSituation: '',
    questions: '',
    hasInsurance: true,
    insuranceType: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const consultationTypes = [
    {
      type: 'immediate',
      title: 'Immediate Consultation',
      description: 'Speak with a care coordinator right now',
      icon: 'ri-phone-line',
      color: 'red',
      duration: '15-30 minutes',
      availability: 'Available now'
    },
    {
      type: 'scheduled',
      title: 'Scheduled Phone Call',
      description: 'Book a convenient time for detailed discussion',
      icon: 'ri-calendar-line',
      color: 'blue',
      duration: '30-45 minutes',
      availability: 'Next available: Today 2:00 PM'
    },
    {
      type: 'video',
      title: 'Video Consultation',
      description: 'Face-to-face consultation via video call',
      icon: 'ri-video-line',
      color: 'green',
      duration: '45-60 minutes',
      availability: 'Next available: Tomorrow 10:00 AM'
    },
    {
      type: 'home-visit',
      title: 'In-Home Assessment',
      description: 'Comprehensive evaluation at your location',
      icon: 'ri-home-line',
      color: 'purple',
      duration: '60-90 minutes',
      availability: 'Next available: Within 48 hours'
    }
  ];

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
  ];

  const consultationTopics = [
    'General home health information',
    'Specific medical condition support',
    'Insurance and coverage questions',
    'Discharge planning from hospital',
    'Caregiver selection and matching',
    'Emergency care coordination',
    'Long-term care planning',
    'Family caregiver support'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImmediateConsultation = () => {
    // Trigger the Readdy Agent widget for immediate consultation
    document.querySelector('#vapi-widget-floating-button')?.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/request-callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          ...formData,
          consultationType,
          selectedTime,
          subject: `${consultationType} consultation request`
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting consultation request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 via-blue-600 to-teal-700 text-white py-24 pt-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/17ce32db5848d468ac809362faec9f9b.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-block bg-white/20 text-white px-8 py-4 rounded-full text-xl font-semibold backdrop-blur-sm shadow-lg">
                💬 Expert Care Consultation
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-8">
              Speak with Our Care Experts
            </h1>
            <p className="text-2xl text-teal-100 max-w-4xl mx-auto leading-relaxed">
              Get personalized guidance from our experienced care coordinators. We'll help you navigate your healthcare options and find the perfect solution for your needs.
            </p>
          </div>
        </div>
      </section>

      {/* Consultation Types */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Choose Your Consultation Type</h2>
            <p className="text-xl text-gray-600">Select the option that works best for your schedule and needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {consultationTypes.map((consultation, index) => (
              <div
                key={index}
                onClick={() => consultation.type === 'immediate' ? handleImmediateConsultation() : setConsultationType(consultation.type)}
                className={`bg-white rounded-3xl shadow-xl p-10 text-center cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-2xl border-2 ${
                  consultationType === consultation.type
                    ? `border-${consultation.color}-500 bg-${consultation.color}-50`
                    : 'border-gray-100 hover:border-gray-300'
                }`}
              >
                <div className={`w-24 h-24 bg-gradient-to-br from-${consultation.color}-500 to-${consultation.color}-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl`}>
                  <i className={`${consultation.icon} text-white text-4xl`}></i>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{consultation.title}</h3>
                <p className="text-gray-600 mb-6 text-lg">{consultation.description}</p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-center text-gray-700">
                    <i className="ri-time-line mr-2 text-lg"></i>
                    <span className="font-medium">{consultation.duration}</span>
                  </div>
                  <div className="flex items-center justify-center text-gray-700">
                    <i className="ri-calendar-check-line mr-2 text-lg"></i>
                    <span className="font-medium">{consultation.availability}</span>
                  </div>
                </div>

                {consultation.type === 'immediate' && (
                  <div className="mt-6">
                    <span className="inline-block bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-bold animate-pulse">
                      Available Now!
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      {consultationType && consultationType !== 'immediate' && (
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {submitStatus === 'success' && (
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-10 mb-12">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-600 text-4xl mr-6"></i>
                  <div>
                    <h3 className="text-green-800 font-bold text-2xl">Consultation Scheduled!</h3>
                    <p className="text-green-700 text-lg">Our care coordinator will contact you at your preferred time.</p>
                  </div>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-10 mb-12">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-600 text-4xl mr-6"></i>
                  <div>
                    <h3 className="text-red-800 font-bold text-2xl">Scheduling Error</h3>
                    <p className="text-red-700 text-lg">Please try again or call us directly at (702) 555-1234.</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-12 border border-gray-100">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Schedule Your Consultation</h2>
                <p className="text-xl text-gray-600">Tell us about your needs and preferred consultation time</p>
              </div>

              {/* Contact Information */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                  <i className="ri-user-line mr-4 text-teal-600"></i>
                  Contact Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-lg"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-lg"
                      placeholder="(702) 555-0123"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-lg"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Preferred Contact Method *
                    </label>
                    <select
                      name="preferredContact"
                      value={formData.preferredContact}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-lg pr-8"
                    >
                      <option value="phone">Phone Call</option>
                      <option value="email">Email</option>
                      <option value="text">Text Message</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Scheduling */}
              {(consultationType === 'scheduled' || consultationType === 'video') && (
                <div className="mb-12">
                  <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                    <i className="ri-calendar-line mr-4 text-teal-600"></i>
                    Preferred Time
                  </h3>
                  
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setSelectedTime(time)}
                        className={`p-4 rounded-xl border-2 font-semibold transition-all cursor-pointer ${
                          selectedTime === time
                            ? 'border-teal-500 bg-teal-50 text-teal-700'
                            : 'border-gray-300 hover:border-teal-300 text-gray-700'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Consultation Details */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                  <i className="ri-question-line mr-4 text-teal-600"></i>
                  Consultation Details
                </h3>
                
                <div className="space-y-8">
                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Primary Topic *
                    </label>
                    <select
                      name="consultationTopic"
                      value={formData.consultationTopic}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-lg pr-8"
                    >
                      <option value="">Select consultation topic</option>
                      {consultationTopics.map((topic) => (
                        <option key={topic} value={topic}>{topic}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Current Situation *
                    </label>
                    <textarea
                      name="currentSituation"
                      value={formData.currentSituation}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-lg"
                      placeholder="Briefly describe your current healthcare situation or needs..."
                    />
                    <p className="text-gray-500 mt-2">{formData.currentSituation.length}/500 characters</p>
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Specific Questions or Concerns
                    </label>
                    <textarea
                      name="questions"
                      value={formData.questions}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-lg"
                      placeholder="What specific questions would you like to discuss during the consultation?"
                    />
                    <p className="text-gray-500 mt-2">{formData.questions.length}/500 characters</p>
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Urgency Level *
                    </label>
                    <select
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-lg pr-8"
                    >
                      <option value="routine">Routine (within 48 hours)</option>
                      <option value="urgent">Urgent (within 24 hours)</option>
                      <option value="stat">STAT (immediate)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Insurance Information */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                  <i className="ri-shield-check-line mr-4 text-teal-600"></i>
                  Insurance Information
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="hasInsurance"
                      checked={formData.hasInsurance}
                      onChange={handleInputChange}
                      className="mr-4 h-5 w-5 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                    />
                    <label className="text-lg font-medium text-gray-700">
                      I have health insurance
                    </label>
                  </div>

                  {formData.hasInsurance && (
                    <div>
                      <label className="block font-bold text-gray-700 mb-4 text-lg">
                        Insurance Type
                      </label>
                      <select
                        name="insuranceType"
                        value={formData.insuranceType}
                        onChange={handleInputChange}
                        className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-lg pr-8"
                      >
                        <option value="">Select insurance type</option>
                        <option value="medicare">Medicare</option>
                        <option value="medicaid">Medicaid</option>
                        <option value="medicaid-waiver">Medicaid Waiver</option>
                        <option value="private">Private Insurance</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting || (consultationType !== 'home-visit' && !selectedTime)}
                  className="w-full md:w-auto bg-teal-600 hover:bg-teal-700 whitespace-nowrap text-xl px-12 py-5 shadow-xl"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-3 animate-spin"></i>
                      Scheduling Consultation...
                    </>
                  ) : (
                    <>
                      <i className="ri-calendar-check-line mr-3"></i>
                      Schedule Consultation
                    </>
                  )}
                </Button>
                
                <p className="text-gray-600 mt-8 text-lg">
                  <i className="ri-time-line mr-2"></i>
                  We'll confirm your appointment within 2 hours
                </p>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Choose Our Expert Consultation?</h2>
            <p className="text-xl text-gray-600">Get personalized guidance from experienced healthcare professionals</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center p-10 bg-white rounded-3xl shadow-xl border border-gray-100">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-user-star-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Expert Guidance</h3>
              <p className="text-gray-600 text-lg">Our care coordinators have years of experience helping families navigate complex healthcare decisions.</p>
            </div>

            <div className="text-center p-10 bg-white rounded-3xl shadow-xl border border-gray-100">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-shield-check-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Insurance Navigation</h3>
              <p className="text-gray-600 text-lg">We'll help you understand your coverage options and maximize your benefits for home healthcare services.</p>
            </div>

            <div className="text-center p-10 bg-white rounded-3xl shadow-xl border border-gray-100">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-heart-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Personalized Care</h3>
              <p className="text-gray-600 text-lg">Every consultation is tailored to your unique situation, needs, and preferences for the best possible outcomes.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}